import * as s from "../TableItemStyled";
import { useEffect, useMemo, useState } from "react";
import {
  BasePagination,
  LEVEL_TYPE,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import {
  ActiveAlert,
  AlertViewInfo,
} from "api/interfaces/alertInterface.interface";
import { StatusTxt } from "pages/alert/AlertStyled";
import { getRecorderTimeFormat } from "utils/timeUtil";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { UnderlineText } from "components/atoms/text/Text";
import { commonOrgColumn } from "pages/dashboard/dashboard/WidgetContainer";
import SearchInput from "components/atoms/input/search/SearchInput";
import AccountSelects from "../AccountSelects";
import { TableColumnsType } from "antd";
import { DashboardChartDetailTable } from "components/atoms/table/DashboardChartDetailTable";
import {
  changeSortOrderAntd,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import { postWidgetListAlertHistory } from "api/widgetAPI";
import { PostWidgetAlertHistoryListResponse } from "api/interfaces/widgetInterface.interface";
import { ColumnType } from "antd/es/table/interface";
import { mobileMax } from "components/MediaQuery";
import { useMediaQuery } from "react-responsive";
import TableItemHeader from "../TableItemHeader";
import { Layout } from "react-grid-layout";
import RecorderTitle from "components/blocks/recorder/RecorderTitle";
import AlertViewModal from "pages/alert/modal/view";
import AlertDisplay from "components/blocks/status/AlertDisplay";
import * as mediaQuery from "components/MediaQuery";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import Tabs from "components/atoms/tabs/Tabs";
import { ALERT_CATEGORY_TYPE, AlertCategoryTypeFilter } from "utils/AlertUtil";
import { useInfiniteQuery } from "react-query";
import { commonPaginationPerPage, TableItemName } from "utils/WidgetUtil";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";

interface Props {
  layout: Layout;
  chartLayout: Layout[];
  onDeleteWidget: (key: TableItemName) => void;
  onLayoutChange: (layout: Layout[]) => void;
}

export default function AlertHistory(props: Props): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const isMobile = useMediaQuery({ maxWidth: mobileMax });

  const [currentMenu, setCurrentMenu] = useState<ALERT_CATEGORY_TYPE>(
    ALERT_CATEGORY_TYPE.VIDEO
  );
  const [accountIds, setAccountIds] = useState<string[]>([]);

  const [isViewModal, setIsViewModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<
    AlertViewInfo | undefined
  >();
  const initialQueryInfo = {
    ...baseQueryInfo,
    // pageLimit: 100,
    pageLimit: commonPaginationPerPage,
    sortType: "alertTrigger",
    sortDirection: SORT_DIRECTION.DESC,
  };

  const [alertActiveQueryInfo, setAlertActiveQueryInfo] =
    useState<BasePagination>({ ...initialQueryInfo });
  const { pageNum, ...alertActiveQueryKey } = useQueryKey(alertActiveQueryInfo);

  const {
    data,
    isLoading: isInitialLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    refetch,
    isFetching,
  } = useInfiniteQuery(
    [
      "postWidgetListAlertHistory",
      alertActiveQueryKey,
      currentMenu,
      selectedAccount.accountId,
      accountIds,
    ],

    ({ pageParam = 0 }) =>
      postWidgetListAlertHistory({
        accountId: selectedAccount.accountId,
        payload: {
          accountIds,
          type: currentMenu,
          listDto: {
            ...alertActiveQueryInfo,
            pageNum: pageNum === 0 ? 0 : pageParam,
          },
        },
      }),
    {
      getNextPageParam: (
        lastPage: PostWidgetAlertHistoryListResponse | undefined
      ) => {
        if (!lastPage || !lastPage.page) {
          return undefined;
        }
        const { total, pageNum, pageLimit } = lastPage.page;
        const nextPage = pageNum + 1;
        return (pageNum + 1) * pageLimit < total ? nextPage : undefined;
      },
      refetchOnWindowFocus: false,
      retry: 0,
      onSuccess: (res: any) => {
        const last = res.pages[res.pages.length - 1];
        if (last) {
          setAlertActiveQueryInfo((info) => {
            return {
              ...info,
              ...last.page,
              pageNum: last.page.pageNum + 1,
            };
          });
        }
      },
    }
  );
  useStompDebouncedRefetch({
    stompKey: ["alertListUpdated"] as StompDateSignalKey[],
    refetch: refetch,
  });
  const onRowClick = (row: ActiveAlert) => {
    // console.log(row);
    setIsViewModal(!isViewModal);
    setSelectedAlert({
      ...row,
      type: row.recorderType,
    });
  };

  const lists = useMemo(() => {
    if (pageNum === 0 && (isInitialLoading || isFetching)) {
      return [];
    }
    return (data?.pages || [])
      .flatMap((page: PostWidgetAlertHistoryListResponse) => page.result ?? [])
      .map((item: ActiveAlert) => ({
        ...item,
        key: item.alertHistoryId,
      }));
  }, [data?.pages, isFetching, isInitialLoading, pageNum]);

  const onHeaderCell = (column: ColumnType<ActiveAlert>) => {
    return {
      onClickCapture: (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (column.key) {
          setAlertActiveQueryInfo((info) => {
            return {
              ...info,
              sortType: column.key as string,
              sortDirection: onSortAntd(info.sortDirection),
              pageNum: 0,
            };
          });
        }
      },
    };
  };

  const columns: TableColumnsType<ActiveAlert> = [
    { ...commonOrgColumn(selectedAccount) },

    {
      title: "Recorder",
      key: "recorderName",
      dataIndex: "recorderName",
      render: (value, row) => (
        <RecorderTitle
          mergedSystemName={row.mergedSystemName}
          recorderName={row.recorderName}
          type={row.recorderType}
        />
      ),
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (value, row) => (
        <StatusTxt string={value.toUpperCase()}>
          {value.toUpperCase()}
        </StatusTxt>
      ),

      hidden: isMobile,
      width: "14%",
      // filterMultiple: false,
      // filters: ActiveAlertStatusTypeFilter.map((fil) => {
      //   return { text: fil.label, value: fil.value };
      // }),
    },
    {
      title: "Alert",
      key: "alertTypeString",
      dataIndex: "alertTypeString",
      render: (value, row) => (
        <>
          <mediaQuery.Mobile>
            <StatusTxt string={row.status.toUpperCase()}>
              {row.status.toUpperCase()}
            </StatusTxt>
          </mediaQuery.Mobile>
          <AlertDisplay
            alertType={row.alertTypeString || String(row.alertType)}
            alertTypeString={row.alertTypeString}
            cameraName={row.cameraName}
            channel={row.cameraChannel}
          />
        </>
      ),

      ellipsis: !isMobile,
      width: selectedAccount.accountLevel === LEVEL_TYPE.EU ? "25%" : "20%",
    },
    {
      title: "Date",
      key: "alertTrigger",
      dataIndex: "alertTrigger",
      render: (value, row) =>
        value !== undefined ? (
          <s.DateWrapper>
            {(
              getRecorderTimeFormat(
                value,
                selectedAccount,
                row.timezone,
                true
              ) || ""
            )
              .split(" ")
              .map((date, index) => (
                <span key={index}>{date}</span>
              ))}
          </s.DateWrapper>
        ) : (
          ""
        ),

      sorter: true,
      sortOrder: changeSortOrderAntd(alertActiveQueryInfo, "alertTrigger"),
      onHeaderCell: onHeaderCell,
      defaultSortOrder: "descend",
      width: selectedAccount.accountLevel === LEVEL_TYPE.EU ? "24%" : "15%",
    },
    {
      title: "",
      render: (value, row) => (
        <UnderlineText
          onClick={() => onRowClick(row)}
          fontSize={14}
          data-testid="dashboard-dashboard-list-underline-text-row"
        >
          View
        </UnderlineText>
      ),

      align: "right",
      width: 50,
      hidden: !selectedAccount.userPermission.viewAlerts,
    },
  ];

  const onChangeTabMenu = (value: string) => {
    setCurrentMenu(value as ALERT_CATEGORY_TYPE);
    setAlertActiveQueryInfo((info) => {
      return { ...info, pageNum: 0 };
    });
    refetch();
  };
  const onChangeAccount = (value: string[]) => {
    setAlertActiveQueryInfo((quer) => {
      return {
        ...quer,
        pageNum: 0,
      };
    });
    setAccountIds(value);
    refetch();
  };

  const onClickSearch = (keyword: string) => {
    setAlertActiveQueryInfo((quer) => {
      return {
        ...initialQueryInfo,
        keyword,
      };
    });
  };

  useEffect(() => {
    if (props.layout.h >= 2) {
      setAlertActiveQueryInfo((info) => {
        return {
          ...info,
          pageLimit: commonPaginationPerPage * props.layout.h,
        };
      });
    } else {
      setAlertActiveQueryInfo((info) => {
        return {
          ...info,
          pageLimit: commonPaginationPerPage,
        };
      });
    }
  }, [props.layout.h]);

  const params = new URLSearchParams({
    recorderId: selectedAlert?.recorderId ?? "",
    alertType: currentMenu,
    historyId: selectedAlert?.alertHistoryId ?? "",
  });

  return (
    <>
      <TableItemHeader
        layout={props.layout}
        chartLayout={props.chartLayout}
        onDeleteWidget={props.onDeleteWidget}
        onLayoutChange={props.onLayoutChange}
        isError={isError}
      />

      <s.Contents className="drag-cancel-content">
        <s.ContentWrapper>
          <s.ContentHeader>
            <Tabs
              tabs={AlertCategoryTypeFilter}
              currentTab={currentMenu}
              onChangeTab={onChangeTabMenu}
            />

            <div>
              <AccountSelects onChange={onChangeAccount} value={accountIds} />
              <SearchInput onClickSearch={onClickSearch} />
            </div>
          </s.ContentHeader>
          <s.ContentInner>
            <DashboardChartDetailTable
              data={lists}
              loadMore={() => fetchNextPage()}
              isLoading={isInitialLoading || isFetchingNextPage}
              hasMore={hasNextPage && !isFetchingNextPage && !isInitialLoading}
              columns={columns}
            />
          </s.ContentInner>
        </s.ContentWrapper>
      </s.Contents>
      {isViewModal && selectedAlert && (
        <AlertViewModal
          searchParams={params}
          onModal={() => setIsViewModal(!isViewModal)}
        />
      )}
    </>
  );
}
